/**
 * OPEN-109 — the support address shown to users, from the platform setting.
 *
 * `useSupportEmail()` fetches GET /v1/public/support-contact (unauthenticated, so it works on
 * the sign-in screen) once per page load, and returns DEFAULT_SUPPORT_EMAIL while loading, on
 * any error, or outside an ApiOptsProvider. A help line must never be blank or broken because
 * the API is unreachable, which is exactly when someone needs it.
 */

import * as React from 'react';
import { useApiOptsOrNull } from '../api/api-opts-context.js';

/** Mirror of DEFAULT_SUPPORT_EMAIL in @lodex-pro/shared (not imported: the barrel pulls drizzle). */
export const DEFAULT_SUPPORT_EMAIL = 'support@lodexpro.com';

let cached: Promise<string> | null = null;

/** Tests only. */
export function resetSupportEmailCacheForTests(): void {
  cached = null;
}

async function fetchSupportEmail(baseUrl: string): Promise<string> {
  try {
    const res = await fetch(`${baseUrl}/v1/public/support-contact`);
    if (!res.ok) return DEFAULT_SUPPORT_EMAIL;
    const body = (await res.json()) as { supportEmail?: unknown };
    return typeof body.supportEmail === 'string' && body.supportEmail.includes('@')
      ? body.supportEmail
      : DEFAULT_SUPPORT_EMAIL;
  } catch {
    return DEFAULT_SUPPORT_EMAIL;
  }
}

export function useSupportEmail(): string {
  const opts = useApiOptsOrNull();
  const [email, setEmail] = React.useState(DEFAULT_SUPPORT_EMAIL);
  React.useEffect(() => {
    if (opts === null) return;
    let live = true;
    cached ??= fetchSupportEmail(opts.baseUrl);
    void cached.then((e) => {
      if (live) setEmail(e);
    });
    return (): void => {
      live = false;
    };
  }, [opts]);
  return email;
}

/** "Need help? Contact <support address>", with a mailto link. */
export function SupportContactLine(): React.JSX.Element {
  const email = useSupportEmail();
  return (
    <p>
      Need help? Contact <a href={`mailto:${email}`}>{email}</a>
    </p>
  );
}
